/**
 * Enum registry — the single place the UI learns what an enum value *means*.
 *
 * Each entry pairs a value tuple imported from `@ias/shared` with an i18n namespace
 * and a tone map. Everything downstream (badges, filter dropdowns, form selects, chart
 * legends, bulk-action menus) is generated from these entries, so:
 *
 *   • adding a value in the shared package makes it appear everywhere automatically
 *   • a status colour is decided once, not re-guessed per screen
 *   • no screen ever contains the literal string 'renewal_due'
 *
 * Tones come from the fixed status roles (good / warning / serious / critical /
 * info / neutral) and never from the categorical chart slots — a status must not be
 * mistakable for a data series.
 */

import {
  ACTIVITY_DIRECTIONS,
  ACTIVITY_TYPES,
  AUDIT_ACTIONS,
  CLAIM_STATUSES,
  CLAIM_TIMELINE_ACTIONS,
  CLIENT_DOCUMENT_TYPES,
  CLIENT_TYPES,
  COMMISSION_STATUSES,
  ENDORSEMENT_TYPES,
  INSTALLMENT_STATUSES,
  INSURANCE_TYPES,
  INSURED_ITEM_TYPES,
  PAYMENT_METHODS,
  PAYMENT_STATUSES,
  RENEWAL_NOTICE_STATUSES,
  INSURER_DOCUMENT_TYPES,
  POLICY_DOCUMENT_TYPES,
  POLICY_STATUSES,
  REMINDER_PRIORITIES,
  REMINDER_STATUSES,
  RENEWAL_FREQUENCIES,
  SETTLEMENT_STATUSES,
  USER_ROLES,
  VEHICLE_CATEGORIES,
} from '@ias/shared';

import type { BadgeTone } from '@/components/ui/badge';

export interface EnumDefinition<T extends string = string> {
  /** i18n namespace: labels resolve as `enum.<namespace>.<value>`. */
  namespace: string;
  values: readonly T[];
  tones?: Partial<Record<T, BadgeTone>>;
  /** Tone used for values with no explicit mapping. */
  defaultTone?: BadgeTone;
}

function define<T extends string>(definition: EnumDefinition<T>): EnumDefinition<T> {
  return definition;
}

export const ENUMS = {
  role: define({
    namespace: 'role',
    values: USER_ROLES,
    tones: { admin: 'info', agent: 'neutral', viewer: 'outline' },
  }),

  /**
   * Identity, not status — so it is told apart by fill vs. outline rather than by hue.
   * Spending the accent on "this client is a company" put blue in every row of the
   * client list to communicate something no one is scanning for.
   */
  clientType: define({
    namespace: 'clientType',
    values: CLIENT_TYPES,
    tones: { individual: 'neutral', company: 'outline' },
  }),

  insuranceType: define({ namespace: 'insuranceType', values: INSURANCE_TYPES, defaultTone: 'outline' }),

  frequency: define({ namespace: 'frequency', values: RENEWAL_FREQUENCIES, defaultTone: 'outline' }),

  /** Spec §8.4 colour rules: active green, renewal_due orange, expired red, cancelled gray. */
  policyStatus: define({
    namespace: 'policyStatus',
    values: POLICY_STATUSES,
    tones: {
      active: 'good',
      pending: 'neutral',
      renewal_due: 'serious',
      expired: 'critical',
      cancelled: 'neutral',
      // Settled and unremarkable. `active` already carries the green; a second
      // coloured "everything is fine" tone only competes with it.
      paid: 'neutral',
    },
  }),

  paymentStatus: define({
    namespace: 'paymentStatus',
    values: PAYMENT_STATUSES,
    tones: { unpaid: 'critical', partially_paid: 'warning', paid: 'good' },
  }),

  /**
   * Ειδοποιητήριο state. The tones encode work outstanding, not good/bad news:
   * `pending` is the loudest because an un-made call is the only one of the four that can
   * still cost a renewal, and `declined` is neutral rather than critical — the client saying
   * no is a closed item for this board and a win-back item for another.
   */
  renewalNoticeStatus: define({
    namespace: 'renewalNoticeStatus',
    values: RENEWAL_NOTICE_STATUSES,
    tones: { pending: 'critical', notified: 'warning', confirmed: 'good', declined: 'neutral' },
  }),

  /** Spec §8.8 colour rules for claims. */
  claimStatus: define({
    namespace: 'claimStatus',
    values: CLAIM_STATUSES,
    tones: {
      // A newly opened claim is the resting state of the list, not an alert; the
      // escalation lives in in_progress / waiting_documents / rejected.
      open: 'neutral',
      in_progress: 'serious',
      waiting_documents: 'warning',
      approved: 'good',
      paid: 'good',
      rejected: 'critical',
      closed: 'neutral',
    },
  }),

  reminderStatus: define({
    namespace: 'reminderStatus',
    values: REMINDER_STATUSES,
    tones: { pending: 'neutral', completed: 'good', overdue: 'critical' },
  }),

  /**
   * `normal` is the default value of the field, so it is on the majority of reminders.
   * Colouring the default is the fastest way to make a colour mean nothing — only the
   * deviation from normal is worth a tone.
   */
  priority: define({
    namespace: 'priority',
    values: REMINDER_PRIORITIES,
    tones: { low: 'outline', normal: 'neutral', high: 'serious' },
  }),

  installmentStatus: define({
    namespace: 'installmentStatus',
    values: INSTALLMENT_STATUSES,
    tones: { pending: 'neutral', paid: 'good', overdue: 'critical', cancelled: 'outline' },
  }),

  paymentMethod: define({ namespace: 'paymentMethod', values: PAYMENT_METHODS, defaultTone: 'outline' }),

  commissionStatus: define({
    namespace: 'commissionStatus',
    values: COMMISSION_STATUSES,
    tones: { expected: 'outline', statement_received: 'neutral', settled: 'good', disputed: 'critical' },
  }),

  settlementStatus: define({
    namespace: 'settlementStatus',
    values: SETTLEMENT_STATUSES,
    tones: { draft: 'neutral', reconciling: 'info', closed: 'good' },
  }),

  /**
   * The vehicle's tariff category. Outline throughout on purpose: it is a classification the
   * commission rate is looked up by, not a state anything is going right or wrong in, and a
   * hue here would compete with the row's own policy status.
   */
  vehicleCategory: define({ namespace: 'vehicleCategory', values: VEHICLE_CATEGORIES, defaultTone: 'outline' }),

  endorsementType: define({ namespace: 'endorsementType', values: ENDORSEMENT_TYPES, defaultTone: 'outline' }),
  insuredItemType: define({ namespace: 'insuredItemType', values: INSURED_ITEM_TYPES, defaultTone: 'outline' }),
  clientDocumentType: define({ namespace: 'clientDocumentType', values: CLIENT_DOCUMENT_TYPES, defaultTone: 'outline' }),
  policyDocumentType: define({ namespace: 'policyDocumentType', values: POLICY_DOCUMENT_TYPES, defaultTone: 'outline' }),
  insurerDocumentType: define({ namespace: 'insurerDocumentType', values: INSURER_DOCUMENT_TYPES, defaultTone: 'outline' }),
  timelineAction: define({ namespace: 'timelineAction', values: CLAIM_TIMELINE_ACTIONS, defaultTone: 'outline' }),
  activityType: define({ namespace: 'activityType', values: ACTIVITY_TYPES, defaultTone: 'outline' }),
  direction: define({ namespace: 'direction', values: ACTIVITY_DIRECTIONS, defaultTone: 'outline' }),

  auditAction: define({
    namespace: 'auditAction',
    values: AUDIT_ACTIONS,
    // `update` is most of an audit log by volume; leaving it neutral is what lets the
    // rare `delete` actually stand out in the column.
    tones: { create: 'good', update: 'neutral', delete: 'critical', login: 'outline', export: 'outline', import: 'outline' },
  }),
} as const;

export type EnumName = keyof typeof ENUMS;

export function toneOf(name: EnumName, value: string | null | undefined): BadgeTone {
  if (!value) return 'neutral';
  const definition = ENUMS[name] as EnumDefinition;
  return definition.tones?.[value] ?? definition.defaultTone ?? 'neutral';
}

export function valuesOf(name: EnumName): readonly string[] {
  return ENUMS[name].values;
}

export function namespaceOf(name: EnumName): string {
  return ENUMS[name].namespace;
}
